import { Region } from '../types';

export interface ServiceLink {
  href: string;
  label: string;
  description?: string;
}

export interface ServiceCategory {
  title: string;
  services: ServiceLink[];
}

export interface CategorizedServices {
  marketing: ServiceCategory;
  system: ServiceCategory;
}

export const serviceTranslations: Record<Region, {
  linkedinAds: ServiceLink;
  personalBranding: ServiceLink;
  corporateInfluencer: ServiceLink;
  abm: ServiceLink;
  hubspotRevops: ServiceLink;
  telemarketing: ServiceLink;
  mailKampagne: ServiceLink;
  motionAi: ServiceLink;
  n8nAutomations: ServiceLink;
  dmOutbound: ServiceLink;
  ipTracking: ServiceLink;
}> = {
  de: {
    linkedinAds: { 
      href: '/de/linkedin-ads', 
      label: 'LinkedIn Ads & Microsoft Ads',
      description: 'Zielgruppenspezifische Werbung für B2B-Entscheider mit messbarem Pipeline-Impact'
    },
    personalBranding: { 
      href: '/de/personal-branding', 
      label: 'Personal Branding',
      description: 'Positionierung als Thought Leader mit strategischem LinkedIn-Profil und Content'
    },
    corporateInfluencer: { 
      href: '/de/corporate-influencer', 
      label: 'Corporate Influencer',
      description: 'Ihre Mitarbeitenden als authentische Markenbotschafter auf LinkedIn'
    },
    abm: { 
      href: '/de/abm', 
      label: 'Account-Based Marketing',
      description: 'Gezielte Ansprache von Wunschkunden mit personalisierten Multi-Channel-Kampagnen'
    },
    hubspotRevops: { 
      href: '/de/hubspot-revops', 
      label: 'HubSpot & RevOps',
      description: 'CRM-Setup, Sales-Automation und Revenue Operations aus einer Hand'
    },
    telemarketing: { 
      href: '/de/telemarketing', 
      label: 'Telemarketing',
      description: 'Qualifizierte Termine durch strukturierte telefonische Nachverfolgung'
    },
    mailKampagne: { 
      href: '/de/mail-kampagne', 
      label: 'Mail-Kampagnen',
      description: 'E-Mail-Sequenzen mit integriertem Telemarketing-Follow-up'
    },
    motionAi: { 
      href: '/de/motion-ai', 
      label: 'Motion AI',
      description: 'KI-gestützte Produktivität mit automatisierter Kalender- und Aufgabenplanung'
    },
    n8nAutomations: { 
      href: '/de/n8n-automations', 
      label: 'n8n-Automatisierungen',
      description: 'Workflow-Automatisierung für nahtlose Tool-Integration'
    },
    dmOutbound: { 
      href: '/de/dm-outbound', 
      label: 'DM Outbound',
      description: 'Persönliche LinkedIn-Direktnachrichten für warme Erstgespräche'
    },
    ipTracking: { 
      href: '/de/ip-tracking', 
      label: 'IP-Tracking',
      description: 'Identifizieren Sie Website-Besucher und nutzen Sie Intent-Daten für ABM'
    },
  },
  ch: {
    linkedinAds: { 
      href: '/ch/linkedin-ads', 
      label: 'LinkedIn Ads & Microsoft Ads',
      description: 'Zielgruppenspezifische Werbung für B2B-Entscheider mit messbarem Pipeline-Impact'
    },
    personalBranding: { 
      href: '/ch/personal-branding', 
      label: 'Personal Branding',
      description: 'Positionierung als Thought Leader mit strategischem LinkedIn-Profil und Content'
    },
    corporateInfluencer: { 
      href: '/ch/corporate-influencer', 
      label: 'Corporate Influencer',
      description: 'Ihre Mitarbeitenden als authentische Markenbotschafter auf LinkedIn'
    },
    abm: { 
      href: '/ch/abm', 
      label: 'Account-Based Marketing',
      description: 'Gezielte Ansprache von Wunschkunden mit personalisierten Multi-Channel-Kampagnen'
    },
    hubspotRevops: { 
      href: '/ch/hubspot-revops', 
      label: 'HubSpot & RevOps',
      description: 'CRM-Setup, Sales-Automation und Revenue Operations aus einer Hand'
    },
    telemarketing: { 
      href: '/ch/telemarketing', 
      label: 'Telemarketing',
      description: 'Qualifizierte Termine durch strukturierte telefonische Nachverfolgung'
    },
    mailKampagne: { 
      href: '/ch/mail-kampagne', 
      label: 'Mail-Kampagnen',
      description: 'E-Mail-Sequenzen mit integriertem Telemarketing-Follow-up'
    },
    motionAi: { 
      href: '/ch/motion-ai', 
      label: 'Motion AI',
      description: 'KI-gestützte Produktivität mit automatisierter Kalender- und Aufgabenplanung'
    },
    n8nAutomations: { 
      href: '/ch/n8n-automations', 
      label: 'n8n-Automatisierungen',
      description: 'Workflow-Automatisierung für nahtlose Tool-Integration'
    },
    dmOutbound: { 
      href: '/ch/dm-outbound', 
      label: 'DM Outbound',
      description: 'Persönliche LinkedIn-Direktnachrichten für warme Erstgespräche'
    },
    ipTracking: { 
      href: '/ch/ip-tracking', 
      label: 'IP-Tracking',
      description: 'Identifizieren Sie Website-Besucher und nutzen Sie Intent-Daten für ABM'
    },
  },
  en: {
    linkedinAds: { 
      href: '/en/linkedin-ads', 
      label: 'LinkedIn Ads & Microsoft Ads',
      description: 'Targeted advertising for B2B decision-makers with measurable pipeline impact'
    },
    personalBranding: { 
      href: '/en/personal-branding', 
      label: 'Personal Branding',
      description: 'Position yourself as a thought leader with strategic LinkedIn presence'
    },
    corporateInfluencer: { 
      href: '/en/corporate-influencer', 
      label: 'Corporate Influencer',
      description: 'Turn your employees into authentic brand ambassadors on LinkedIn'
    },
    abm: { 
      href: '/en/abm', 
      label: 'Account-Based Marketing',
      description: 'Target ideal accounts with personalized multi-channel campaigns'
    },
    hubspotRevops: { 
      href: '/en/hubspot-revops', 
      label: 'HubSpot & RevOps',
      description: 'CRM setup, sales automation, and revenue operations done right'
    },
    telemarketing: { 
      href: '/en/telemarketing', 
      label: 'Telemarketing',
      description: 'Qualified meetings through structured phone follow-up'
    },
    mailKampagne: { 
      href: '/en/mail-campaigns', 
      label: 'Mail Campaigns',
      description: 'Email sequences with integrated telemarketing follow-up'
    },
    motionAi: { 
      href: '/en/motion-ai', 
      label: 'Motion AI',
      description: 'AI-powered productivity with automated calendar and task planning'
    },
    n8nAutomations: { 
      href: '/en/n8n-automations', 
      label: 'n8n Automations',
      description: 'Workflow automation for seamless tool integration'
    },
    dmOutbound: { 
      href: '/en/dm-outbound', 
      label: 'DM Outbound',
      description: 'Personal LinkedIn direct messages for warm first conversations'
    },
    ipTracking: { 
      href: '/en/ip-tracking', 
      label: 'IP Tracking',
      description: 'Identify website visitors and leverage intent data for ABM'
    },
  },
};

export const getCategorizedServices = (region: Region): CategorizedServices => {
  const t = serviceTranslations[region];
  const isEnglish = region === 'en';
  
  return {
    marketing: {
      title: isEnglish ? 'Marketing' : 'Marketing',
      services: [
        t.linkedinAds,
        t.personalBranding,
        t.corporateInfluencer,
        t.abm,
        t.ipTracking,
        t.telemarketing,
        t.mailKampagne,
        t.dmOutbound,
      ],
    },
    system: {
      title: isEnglish ? 'System & Tools' : 'System & Tools',
      services: [
        t.hubspotRevops,
        t.motionAi,
        t.n8nAutomations,
      ],
    },
  };
};

// Keep for backwards compatibility
export const getServiceLinks = (region: Region): ServiceLink[] => {
  const t = serviceTranslations[region];
  return [
    t.linkedinAds,
    t.personalBranding,
    t.corporateInfluencer,
    t.abm,
    t.ipTracking,
    t.hubspotRevops,
    t.telemarketing,
    t.mailKampagne,
    t.motionAi,
    t.n8nAutomations,
    t.dmOutbound,
  ];
};
